import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, PlusCircle, FolderClock, BookOpen, CreditCard, LifeBuoy, Shield, LogOut, AlertCircle, Menu, X, Syringe, Lightbulb, Hourglass, Building2, PlayCircle, Siren } from "lucide-react";
import { EmergencySheet } from "./emergency/EmergencySheet";
import { useState } from "react";
import { Button } from "./ui/button";
import { LogoSymbol, LogoHorizontal } from "./Logo";
import { ShareButton } from "./ShareButton";
import api from "../lib/api";
import { toast } from "sonner";

const nav = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", exact: true },
  { to: "/app/new", label: "New Evaluation", icon: PlusCircle, testid: "nav-new" },
  { to: "/app/emergency", label: "Emergency Protocols", icon: Siren, testid: "nav-emergency" },
  { to: "/app/anesthetic", label: "Anaesthetic Calc", icon: Syringe, testid: "nav-anesthetic" },
  { to: "/app/pending", label: "Pending Cases", icon: Hourglass, testid: "nav-pending" },
  { to: "/app/cases", label: "Case History", icon: FolderClock, testid: "nav-cases" },
  { to: "/app/knowledge", label: "Content Base", icon: BookOpen, testid: "nav-knowledge" },
  { to: "/app/account", label: "My Account", icon: CreditCard, testid: "nav-account" },
  { to: "/app/organization", label: "Organization", icon: Building2, testid: "nav-organization" },
  { to: "/app/suggest", label: "Suggest an Addition", icon: Lightbulb, testid: "nav-suggest" },
  { to: "/app/tutorial", label: "How to use", icon: PlayCircle, testid: "nav-tutorial" },
  { to: "/app/support", label: "Support", icon: LifeBuoy, testid: "nav-support" },
];

function TrialBanner({ user }) {
  const st = user?.subscription_status;
  if (st !== "trial" && st !== "expired") return null;
  const left = st === "trial" && user.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - Date.now()) / 86400000)) : 0;
  const urgent = st === "expired" || left <= 2;
  return (
    <div data-testid="trial-banner" className={`border-b px-4 lg:px-8 py-2.5 flex items-center gap-3 flex-wrap ${urgent ? "bg-[#0B2545] border-[#0B2545] text-white" : "bg-sky-50 border-sky-200 text-sky-900"}`}>
      <span className="text-sm" data-testid="trial-banner-text">
        {st === "expired" ? "Your free trial has ended." : <>Free trial: <b>{left} day{left === 1 ? "" : "s"} left</b>.</>} Subscribe for $10/month or $100/year — cancel anytime.
      </span>
      <Link to="/app/account" data-testid="trial-banner-subscribe" className={`text-sm font-semibold rounded-full px-3 py-1 transition-colors ${urgent ? "bg-[#1C6FE3] hover:bg-[#1659b8]" : "bg-[#0B2545] text-white hover:bg-[#13315C]"}`}>Subscribe</Link>
    </div>
  );
}

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isActive = (item) => (item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to));

  const resendVerify = async () => {
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent");
    } catch {
      toast.error("Could not send verification email");
    }
  };

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const NavLinks = () => (
    <nav className="flex flex-col gap-1 px-3">
      {nav.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          data-testid={item.testid}
          onClick={() => setOpen(false)}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            isActive(item) ? "bg-sky-50 text-sky-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          }`}
        >
          <item.icon className="w-[18px] h-[18px]" />
          {item.label}
        </Link>
      ))}
      {user?.role === "admin" && (
        <Link
          to="/admin"
          data-testid="nav-admin"
          onClick={() => setOpen(false)}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            location.pathname.startsWith("/admin") ? "bg-sky-50 text-sky-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          }`}
        >
          <Shield className="w-[18px] h-[18px]" />
          Admin Panel
        </Link>
      )}
      <div className="pt-3 mt-2 border-t border-slate-100">
        <ShareButton label="Share with a colleague" className="w-full justify-start text-sky-700 border-sky-200" testid="sidebar-share-btn" />
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 flex-col">
        <Link to="/app" className="flex items-center gap-2.5 px-6 h-24 border-b border-slate-200">
          <LogoHorizontal size={64} />
        </Link>
        <div className="flex-1 py-4 overflow-y-auto">
          <NavLinks />
        </div>
        <div className="p-3 border-t border-slate-200">
          <div className="px-3 py-2">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
          <Button variant="ghost" onClick={doLogout} data-testid="logout-btn" className="w-full justify-start text-slate-600 hover:text-red-600">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between h-20 px-4 bg-white border-b border-slate-200">
        <Link to="/app" className="flex items-center gap-2">
          <LogoHorizontal size={52} />
        </Link>
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="p-2 text-slate-700">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 top-20 z-30 bg-white">
          <div className="py-4"><NavLinks /></div>
          <div className="px-3 mt-2 border-t border-slate-200 pt-3">
            <Button variant="ghost" onClick={doLogout} className="w-full justify-start text-slate-600">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="lg:pl-64">
        {user && !user.is_verified && (
          <div data-testid="verify-banner" className="bg-amber-50 border-b border-amber-200 px-4 lg:px-8 py-2.5 flex items-center gap-2 flex-wrap">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-800">Please verify your email to secure your account.</span>
            <button onClick={resendVerify} className="text-sm font-semibold text-amber-900 underline" data-testid="resend-verify-btn">Resend email</button>
          </div>
        )}
        <TrialBanner user={user} />
        <div className="px-4 lg:px-8 py-6 lg:py-8 pb-24 max-w-6xl mx-auto">{children}</div>
      </main>
      <EmergencySheet />
    </div>
  );
}
